"use client";

import { Button } from "@/components/atoms/buttons";
import { toast } from "@/components/atoms/toast";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import { sdk } from "@/lib/medusa/client";
import Link from "next/link";
import { useEffect, useState, useTransition } from "react";
import { StoreCustomer } from "@medusajs/types";

type VerfiyEmailButtonProps = {
  locale: Locales;
  customerId: string;
};

const VerifyEmailButton = ({ locale, customerId }: VerfiyEmailButtonProps) => {
  const [isPending, startTransition] = useTransition();
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [customer, setCustomer] = useState<StoreCustomer | undefined>();

  useEffect(() => {
    startTransition(async () => {
      const fetchCustomer = async () => {
        try {
          const response = (await sdk.client.fetch(`store/custom/customer/${customerId}`)) as { data: StoreCustomer };
          setCustomer(response.data);
        } catch (error) {
          console.error("Error fetching customer:", error);
          toast.error({ title: __("Something went wrong, Please try again later.", locale) });
        }
      };
      if (customerId) {
        fetchCustomer();
      }
    });
  }, [customerId, locale]);

  const handleVerifyEmail = () => {
    if (!customer?.id) return;
    startTransition(async () => {
      try {
        const redirectUrl = window.location.origin + getPermalink(locale, "signup", "confirm");
        setIsSuccessful(false);
        await sdk.client.fetch(`/store/custom/customer/${customer.id}/verify/request`, {
          method: "POST",
          body: {
            url: redirectUrl,
            lang: locale,
          },
        });
        setIsSuccessful(true);
      } catch (error) {
        if (error instanceof SyntaxError && error.message.includes("Unexpected end of JSON input")) {
          setIsSuccessful(true);
          return;
        }
        setIsSuccessful(false);
        console.error("Error verifying email:", error);
        toast.error({ title: __("Something went wrong, Please try again later.", locale) });
      }
    });
  };

  return (
    <div className="font-cabinet-grotesk w-full space-y-6">
      <div className="w-full">
        <h2 className="text-2xl font-bold">{__("Let's verify your email address", locale)}</h2>
        {!isSuccessful ? (
          <p className="text-primary-light">{__("Click on this button to verify you email address", locale)}</p>
        ) : (
          <p className="text-primary-light">
            {__("We have sent you a verification email to ", locale)}{" "}
            <Link href={`mailto:${customer?.email}`} target="_blank" className="text-pink-primary">
              {customer?.email}
            </Link>
            {__(" Please check your inbox.", locale)}
          </p>
        )}
      </div>
      <Button
        disabled={isPending || isSuccessful || (customer?.metadata?.isVerified as boolean)}
        onClick={handleVerifyEmail}
        className="h-11 w-full cursor-pointer"
      >
        {__("Verify", locale)}{" "}
      </Button>
      <p className="text-sm">
        {__("Already registered ?", locale)}
        <Link href={getPermalink(locale, "login")} className="text-pink-primary">
          {__("Login", locale)}
        </Link>
      </p>
    </div>
  );
};

export default VerifyEmailButton;
